import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { GameResult } from "@topzle/types";
import { z } from "zod";
import drawGuessWordData from "./draw-guess-words.json";

export const drawGuessWords = drawGuessWordData;

const pointSchema = z.tuple([z.number().int().min(0).max(1000), z.number().int().min(0).max(1000)]);
const strokeSchema = z.object({
  id: z.string().min(1).max(80),
  mode: z.enum(["draw", "erase"]).default("draw"),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  size: z.number().int().min(2).max(48),
  points: z.array(pointSchema).min(1).max(64),
});
const guessSchema = z.object({
  playerId: z.string(),
  text: z.string().min(1).max(48),
  correct: z.boolean(),
  confidence: z.number().min(0).max(1).nullable(),
  at: z.number(),
});

export const drawGuessStateSchema = z.object({
  players: z.array(z.string()).min(2).max(12),
  humanPlayers: z.array(z.string()).min(1).max(12),
  drawerIndex: z.number().int().min(0).max(11),
  drawerId: z.string(),
  word: z.enum(drawGuessWords as [string, ...string[]]),
  round: z.number().int().min(1).max(10),
  totalRounds: z.number().int().min(1).max(10),
  phase: z.enum(["drawing", "round_result", "finished"]),
  roundStartedAt: z.number(),
  deadline: z.number(),
  strokes: z.array(strokeSchema).max(64),
  guesses: z.array(guessSchema).max(100),
  scores: z.record(z.string(), z.number().int().nonnegative()),
  roundWinnerId: z.string().nullable(),
  roundReason: z.enum(["correct_guess", "timeout", "skipped"]).nullable(),
  aiEnabled: z.boolean(),
});

export const drawGuessEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("stroke"), stroke: strokeSchema }),
  z.object({ type: z.literal("undo") }),
  z.object({ type: z.literal("clear") }),
  z.object({ type: z.literal("guess"), guess: z.string().trim().min(1).max(48) }),
  z.object({
    type: z.literal("ai_guess"),
    guess: z.string().trim().min(1).max(48),
    confidence: z.number().min(0).max(1),
  }),
  z.object({ type: z.literal("skip") }),
  z.object({ type: z.literal("next_round") }),
]);

export const drawGuessSettingsSchema = z.object({
  roundSeconds: z.number().int().min(30).max(180).default(60),
  rounds: z.number().int().min(1).max(10).default(3),
});

export type DrawGuessState = z.infer<typeof drawGuessStateSchema>;
export type DrawGuessEvent = z.infer<typeof drawGuessEventSchema>;
export type DrawGuessSettings = z.infer<typeof drawGuessSettingsSchema>;
export type DrawGuessStroke = z.infer<typeof strokeSchema>;

function normalizeGuess(value: string): string {
  return value
    .toLowerCase()
    .replaceAll("&", "and")
    .replace(/[^a-z0-9]/g, "");
}

function isCorrectGuess(guess: string, word: string): boolean {
  return normalizeGuess(guess) === normalizeGuess(word);
}

function nextWord(
  currentWord: string,
  context: { randomInt?: (maximumExclusive: number) => number },
) {
  const currentIndex = drawGuessWords.indexOf(currentWord);
  const offset = (context.randomInt?.(drawGuessWords.length - 1) ?? 0) + 1;
  return drawGuessWords[(Math.max(0, currentIndex) + offset) % drawGuessWords.length] ?? "cat";
}

function finishRound(
  state: DrawGuessState,
  reason: DrawGuessState["roundReason"],
  winnerId: string | null,
  now: number,
): DrawGuessState {
  const scores = { ...state.scores };
  if (winnerId) {
    const remaining = Math.max(0, state.deadline - now);
    const speedBonus = Math.min(500, Math.floor(remaining / 100));
    scores[winnerId] = (scores[winnerId] ?? 0) + 700 + speedBonus;
    scores[state.drawerId] = (scores[state.drawerId] ?? 0) + (state.aiEnabled ? 300 : 500);
  }
  return {
    ...state,
    phase: "round_result",
    scores,
    roundWinnerId: winnerId,
    roundReason: reason,
  };
}

export const drawGuessGame: TopzleGameDefinition<
  DrawGuessState,
  DrawGuessEvent,
  DrawGuessSettings,
  { word: string | null }
> = {
  id: "draw-guess",
  version: "1.0.0",
  name: "Draw & Guess",
  shortDescription: "Draw secret prompts while friends—or an on-device AI—race to guess them.",
  longDescription:
    "A fast drawing party game with server-authoritative rounds and an optional browser-local sketch classifier that never calls an external AI API.",
  category: "party",
  icon: "palette",
  cover: "draw-guess",
  minPlayers: 2,
  maxPlayers: 12,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: true,
  supportsLocalAi: true,
  supportsCloudAi: false,
  supportsTournaments: false,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["bubbles", "rail", "game_fullscreen", "split", "avatar_only"],
  preferredLayout: "bubbles",
  stateSchema: drawGuessStateSchema,
  eventSchema: drawGuessEventSchema,
  settingsSchema: drawGuessSettingsSchema,
  createInitialState(context, settings) {
    const humanPlayers = context.playerIds.filter((id) => !id.startsWith("ai:"));
    const drawerId = humanPlayers[0] ?? context.playerIds[0] ?? "";
    return {
      players: context.playerIds,
      humanPlayers,
      drawerIndex: 0,
      drawerId,
      word: drawGuessWords[context.randomInt?.(drawGuessWords.length) ?? 0] ?? "cat",
      round: 1,
      totalRounds: settings.rounds,
      phase: "drawing",
      roundStartedAt: context.now,
      deadline: context.now + settings.roundSeconds * 1000,
      strokes: [],
      guesses: [],
      scores: Object.fromEntries(context.playerIds.map((id) => [id, 0])),
      roundWinnerId: null,
      roundReason: null,
      aiEnabled: context.playerIds.some((id) => id.startsWith("ai:")),
    };
  },
  validateEvent(state, event, context) {
    if (state.phase === "finished")
      return { valid: false, code: "finished", message: "The match is complete" };
    if (event.type === "next_round")
      return {
        valid: state.phase === "round_result" && context.actorId === state.drawerId,
        code: "drawer_only",
        message: "The drawer starts the next round",
      };
    if (state.phase !== "drawing")
      return { valid: false, code: "round_closed", message: "This round has ended" };
    if (event.type === "stroke" || event.type === "undo" || event.type === "clear")
      return {
        valid: context.actorId === state.drawerId,
        code: "drawer_only",
        message: "Only the drawer can change the canvas",
      };
    if (event.type === "skip")
      return {
        valid: context.actorId === state.drawerId,
        code: "drawer_only",
        message: "Only the drawer can skip this prompt",
      };
    if (event.type === "ai_guess")
      return {
        valid: state.aiEnabled && context.actorId === state.drawerId,
        code: "ai_unavailable",
        message: "Local AI guesses are available only in solo AI mode",
      };
    return {
      valid: context.actorId !== state.drawerId && state.players.includes(context.actorId ?? ""),
      code: "drawer_cannot_guess",
      message: "The drawer cannot submit a guess",
    };
  },
  reduceEvent(state, event, context) {
    if (event.type === "stroke")
      return {
        ...state,
        strokes: [
          ...state.strokes.filter((stroke) => stroke.id !== event.stroke.id),
          event.stroke,
        ].slice(-64),
      };
    if (event.type === "undo") return { ...state, strokes: state.strokes.slice(0, -1) };
    if (event.type === "clear") return { ...state, strokes: [] };
    if (event.type === "skip") return finishRound(state, "skipped", null, context.now);
    if (event.type === "next_round") {
      if (state.round >= state.totalRounds) return { ...state, phase: "finished" };
      const drawerIndex = (state.drawerIndex + 1) % state.humanPlayers.length;
      return {
        ...state,
        drawerIndex,
        drawerId: state.humanPlayers[drawerIndex] ?? state.drawerId,
        word: nextWord(state.word, context),
        round: state.round + 1,
        phase: "drawing",
        roundStartedAt: context.now,
        deadline: context.now + (state.deadline - state.roundStartedAt),
        strokes: [],
        guesses: [],
        roundWinnerId: null,
        roundReason: null,
      };
    }
    const playerId =
      event.type === "ai_guess"
        ? (state.players.find((id) => id.startsWith("ai:")) ?? "ai:local")
        : (context.actorId ?? "");
    const guess = event.guess.trim().toLowerCase();
    const correct = isCorrectGuess(guess, state.word);
    const guesses = [
      ...state.guesses,
      {
        playerId,
        text: guess,
        correct,
        confidence: event.type === "ai_guess" ? event.confidence : null,
        at: context.now,
      },
    ].slice(-100);
    const guessedState = { ...state, guesses };
    return correct
      ? finishRound(guessedState, "correct_guess", playerId, context.now)
      : guessedState;
  },
  getPublicState(state, viewer) {
    return {
      ...state,
      word: viewer.viewerId === state.drawerId || state.phase !== "drawing" ? state.word : null,
    };
  },
  getPrivatePlayerState(state, playerId) {
    return { word: playerId === state.drawerId ? state.word : null };
  },
  getSpectatorState(state) {
    return { ...state, word: state.phase === "drawing" ? null : state.word };
  },
  getAvailableActions(state, playerId) {
    if (state.phase === "round_result")
      return playerId === state.drawerId ? [{ type: "next_round" }] : [];
    if (state.phase !== "drawing") return [];
    if (playerId.startsWith("ai:")) return [];
    if (playerId === state.drawerId)
      return [
        { type: "stroke" },
        { type: "undo" },
        { type: "clear" },
        { type: "skip" },
        ...(state.aiEnabled ? [{ type: "ai_guess" }] : []),
      ];
    return [{ type: "guess" }];
  },
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished") return null;
    const highScore = Math.max(...Object.values(state.scores));
    return {
      status: "completed",
      winnerIds: Object.entries(state.scores)
        .filter(([, score]) => score === highScore)
        .map(([id]) => id),
      scores: state.scores,
      reason: "rounds_complete",
    };
  },
  getNextDeadline(state) {
    return state.phase === "drawing" ? state.deadline : null;
  },
  handleDeadline(state, context) {
    return state.phase === "drawing" && state.deadline <= context.now
      ? finishRound(state, "timeout", null, context.now)
      : state;
  },
  handlePlayerJoin(state, arrival) {
    if (
      state.phase === "finished" ||
      state.players.length >= 12 ||
      state.players.includes(arrival.playerId)
    )
      return null;
    return {
      state: {
        ...state,
        players: [...state.players, arrival.playerId],
        humanPlayers: [...state.humanPlayers, arrival.playerId],
        scores: { ...state.scores, [arrival.playerId]: 0 },
      },
    };
  },
  handlePlayerDeparture(state, departure, context) {
    if (!state.players.includes(departure.playerId)) return null;
    const useReplacement = state.players.length === 2 && departure.replacementPlayerId;
    const replacementPlayerId = useReplacement ? departure.replacementPlayerId : undefined;
    const players = replacementPlayerId
      ? state.players.map((id) => (id === departure.playerId ? replacementPlayerId : id))
      : state.players.filter((id) => id !== departure.playerId);
    const humanPlayers = state.humanPlayers.filter((id) => id !== departure.playerId);
    const scores = { ...state.scores };
    if (replacementPlayerId) scores[replacementPlayerId] = scores[departure.playerId] ?? 0;
    delete scores[departure.playerId];
    const removedDrawer = state.drawerId === departure.playerId;
    const nextDrawerIndex = Math.min(state.drawerIndex, Math.max(0, humanPlayers.length - 1));
    const nextDrawerId = removedDrawer
      ? (humanPlayers[nextDrawerIndex] ?? humanPlayers[0] ?? state.drawerId)
      : state.drawerId;
    const resetRound = removedDrawer && state.phase !== "finished";
    const duration = Math.max(30_000, state.deadline - state.roundStartedAt);
    return {
      state: {
        ...state,
        players,
        humanPlayers,
        scores,
        drawerIndex: nextDrawerIndex,
        drawerId: nextDrawerId,
        word: resetRound ? nextWord(state.word, context) : state.word,
        phase: resetRound ? "drawing" : state.phase,
        roundStartedAt: resetRound ? context.now : state.roundStartedAt,
        deadline: resetRound ? context.now + duration : state.deadline,
        strokes: resetRound ? [] : state.strokes,
        guesses: resetRound ? [] : state.guesses,
        roundWinnerId: state.roundWinnerId === departure.playerId ? null : state.roundWinnerId,
        roundReason: resetRound ? null : state.roundReason,
        aiEnabled: players.some((id) => id.startsWith("ai:")),
      },
      ...(replacementPlayerId ? { replacementPlayerId } : {}),
    };
  },
};
